import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import blogPosts from "../data/blogPosts";
import SEO from "../components/SEO";
import GetStartedBar from "../components/GetStartedBar";

export default function BlogPage() {
  // Sort by publish date, newest first
  const sorted = [...blogPosts].sort(
    (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
  );

  const featured = sorted[0];

  return (
    <div className="bg-bg text-ink">
      <SEO
        title="Blog"
        description="Expert insights on contractor management, compliance, staffing alternatives, and workforce strategy. Practical guides for businesses managing independent contractors."
        path="/blog"
        keywords={["contractor management blog", "1099 compliance guide", "staffing industry insights", "independent contractor best practices"]}
      />
      {/* Hero */}
      <section className="px-4 sm:px-6 pt-14 pb-8 sm:pt-20 sm:pb-10">
        <div className="max-w-5xl mx-auto">
          <div className="font-mono uppercase text-[0.65625rem] tracking-[.24em] text-brand">The Sift Blog</div>
          <h1 className="mt-4 font-sans font-bold tracking-tight leading-[1.06] text-3xl sm:text-4xl lg:text-[3.25rem]">
            Field notes on running <span className="font-display italic font-semibold">the work.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-secondary max-w-3xl leading-relaxed">
            Practical guides on managing independent contractors, reducing staffing costs, and staying
            compliant. Built for the people running contractor operations.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-5xl mx-auto">
          <Link
            to={`/blog/${featured.slug}`}
            className="block group bg-surface border border-line rounded-2xl p-6 sm:p-8 shadow-sift-sm hover:border-brand hover:shadow-sift-md transition-all"
          >
            <span className="inline-block font-mono uppercase text-[0.625rem] tracking-[.2em] text-brand mb-3">
              Featured
            </span>
            <h2 className="font-display italic text-2xl sm:text-3xl text-ink leading-snug group-hover:text-brand transition-colors">
              {featured.title}
            </h2>
            <p className="mt-3 text-secondary leading-relaxed max-w-3xl">
              {featured.excerpt}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-tertiary">
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                {formatDate(featured.publishedDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} />
                {featured.readTime}
              </span>
              <span className="inline-block px-2.5 py-0.5 rounded-pill bg-brand-tint border border-line text-xs text-brand font-medium">
                {featured.category}
              </span>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand group-hover:gap-2.5 transition-all">
              Read article <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* All Posts */}
      <section className="px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display italic text-xl sm:text-2xl text-ink mb-6">All Articles</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-surface border border-line rounded-2xl p-5 shadow-sift-sm hover:border-brand hover:shadow-sift-md transition-all flex flex-col"
              >
                <span className="inline-block self-start px-2.5 py-0.5 rounded-pill bg-brand-tint border border-line text-xs text-brand font-medium mb-3">
                  {post.category}
                </span>
                <h3 className="text-base sm:text-lg font-semibold text-ink leading-snug group-hover:text-brand transition-colors">
                  {post.title}
                </h3>
                <p className="mt-2 text-sm text-secondary leading-relaxed flex-1">
                  {post.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-3 text-xs text-tertiary">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(post.publishedDate)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {post.readTime}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 pt-6 pb-14">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl px-6 sm:px-10 py-12 text-center" style={{ background: "var(--ink)", color: "var(--on-dark)" }}>
            <div aria-hidden="true" style={{ position: "absolute", top: "-40%", left: "50%", transform: "translateX(-50%)", width: 460, height: 460, background: "radial-gradient(50% 50% at 50% 50%, rgba(224,201,136,.18), rgba(224,201,136,0) 70%)", pointerEvents: "none" }} />
            <div className="relative">
              <h2 className="font-sans font-bold tracking-tight text-2xl sm:text-3xl">
                Ready to simplify your contractor operations?
              </h2>
              <p className="mt-3 text-[0.96875rem]" style={{ color: "var(--on-dark-muted)" }}>
                See how Sift can cut costs, reduce risk, and save your team hours every week.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/get-started" className="inline-flex items-center justify-center px-7 py-3.5 rounded-pill bg-brand hover:bg-brand-hover text-white font-semibold transition-colors">
                  Get started
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center px-7 py-3.5 rounded-pill font-semibold transition-colors" style={{ background: "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>
                  Get in touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GetStartedBar message="Ready to put your workforce in one place?" sub="Free to join. Set up in under two minutes." location="blog_sticky_bar" />
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
